import { Pencil } from "lucide-react";
import { Button } from "@primitives/button";
import { useTranslation } from "react-i18next";
import { useToast } from "@/hooks/useToast";
import { useNavigationState } from "@/zustand/NavigationState";
import { CHAT_HUB_ID } from "@/config/constants";
import { ChatType } from "@/server/types/chat";
import { useMemo, useState } from "react";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/primitives/dialog";

import { ScrollArea, ScrollBar } from "@/components/primitives/scroll-area";

import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  ArchitectEditFormSchema,
  GroupChatFormSchema,
  GroupChatFormType,
} from "@/schemas/chat";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/primitives/form";
import { Switch } from "@/components/primitives/switch";
import { Input } from "@/components/primitives/input";
import { useAvatarUploader } from "@/middleware/hooks/useAvatarUploader";
import AvatarUploader from "@/components/shared/AvatarUploader";
import { cn, processAvatarBucketUrl } from "@/lib/utils";
import { updateGroupChat } from "@/middleware/chats";
import ThreadSettingsFields from "../../left-column/action-views/createGroupChat/ThreadSettingsFields";

export default function EditChatDetails() {
  const activeChatId = useNavigationState((state) => state.activeChatId);
  const activeChatType = useNavigationState((state) => state.activeChatType);
  const activeChatHubId = useNavigationState((state) => state.activeChatHubId);
  const activeChatIsPublic = useNavigationState(
    (state) => state.activeChatIsPublic,
  );
  const activeChatAllowThreads = useNavigationState(
    (state) => state.activeChatAllowThreads,
  );
  const activeChatCanPost = useNavigationState(
    (state) => state.activeChatCanPost,
  );
  const activeChatAvatar = useNavigationState(
    (state) => state.activeChatAvatar,
  );
  const activeChatName = useNavigationState((state) => state.activeChatName);
  const activeChatDescription = useNavigationState(
    (state) => state.activeChatDescription,
  );

  const isChatWithArchitect =
    activeChatType === ChatType.GROUP && activeChatHubId === CHAT_HUB_ID;

  const [openDialog, setOpenDialog] = useState<true | false>(false);

  const {
    isUploading,
    blobUrl,
    attachmentId,
    avatarCleared,
    handleFileChange,
    discardBlobUrl,
    clearAvatar,
  } = useAvatarUploader({});

  const { t } = useTranslation();
  const { toast } = useToast();

  const resolver = useMemo(
    () =>
      zodResolver(
        isChatWithArchitect ? ArchitectEditFormSchema : GroupChatFormSchema,
      ),
    [isChatWithArchitect],
  );

  const form = useForm<GroupChatFormType>({
    resolver,
    defaultValues: {
      avatar: "",
      title: activeChatName,
      description: activeChatDescription,
      isSearchable: activeChatIsPublic,
      allowThreads: activeChatAllowThreads,
      canPost: activeChatCanPost,
    },
  });

  const currentAvatarUrl = processAvatarBucketUrl(
    activeChatAvatar?.attachmentId || "",
  );

  async function onSubmit(values: GroupChatFormType) {
    if (!activeChatId) {
      return;
    }
    try {
      // Determine avatar value: new upload > explicit clear > keep existing
      let avatarValue: string;
      if (attachmentId) {
        avatarValue = attachmentId;
      } else if (avatarCleared) {
        avatarValue = "";
      } else {
        avatarValue = activeChatAvatar?.attachmentId || "";
      }

      const payload = {
        ...values,
        avatar: avatarValue,
      };

      await updateGroupChat(activeChatId, payload);

      setOpenDialog(false);
      toast({
        duration: 1000,
        description: t("editChat.updatedSuccessfully"),
      });
    } catch (error) {
      toast({
        duration: 1000,
        description:
          error instanceof Error ? error.message : t("errorOccurred"),
        variant: "destructive",
      });
    }
  }

  if (!activeChatId) {
    return null;
  }

  return (
    <>
      <Button
        onClick={() => setOpenDialog(true)}
        size="icon"
        variant="ghost"
        className="flex size-14 cursor-pointer flex-col items-center gap-1 rounded-md p-1 align-middle text-foreground hover:bg-transparent"
      >
        <Pencil className="size-4" />
        <span className="text-sm font-normal">{t("edit")}</span>
      </Button>

      <Dialog
        open={openDialog}
        onOpenChange={(isOpen) => {
          if (form.formState.isSubmitting || isUploading) {
            return;
          }

          form.reset({
            title: activeChatName,
            avatar: "",
            description: activeChatDescription,
            isSearchable: activeChatIsPublic,
            allowThreads: activeChatAllowThreads,
            canPost: activeChatCanPost,
          });

          discardBlobUrl();

          setOpenDialog(isOpen);
        }}
      >
        <DialogContent className="my-auto flex h-full !min-h-[560px] flex-1 flex-col border-none bg-card md:h-4/5">
          <DialogHeader>
            <DialogTitle>{t("editChat.dialogTitle")}</DialogTitle>
            <DialogDescription />
          </DialogHeader>

          <ScrollArea className="flex h-full flex-1 py-2">
            <div className="flex flex-1 flex-col items-center justify-start gap-1 p-4 align-middle">
              <div className="relative mb-3 cursor-pointer p-3">
                <AvatarUploader
                  handleFileChange={handleFileChange}
                  handleDeleteAvatar={clearAvatar}
                  isUploading={isUploading}
                  blobUrl={blobUrl}
                  name={activeChatName}
                  colorSeed={activeChatId}
                  defaultAvatarUrl={avatarCleared ? "" : currentAvatarUrl}
                />
              </div>

              <Form {...form}>
                <form
                  // onSubmit={form.handleSubmit(onSubmit)}
                  className="flex w-full flex-1 flex-col gap-3"
                >
                  {!isChatWithArchitect && (
                    <FormField
                      control={form.control}
                      name="isSearchable"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-center justify-between gap-3">
                          <div className="space-y-0.5">
                            <FormLabel>{t("searchable")}</FormLabel>
                            <FormDescription className="text-foreground/50">
                              {t("searchableDescription")}
                            </FormDescription>
                          </div>
                          <FormControl>
                            <Switch
                              checked={field.value}
                              onCheckedChange={(checked) => {
                                field.onChange(checked);
                                if (!checked) {
                                  form.setValue("description", "");
                                }
                              }}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  )}
                  <FormField
                    control={form.control}
                    name="title"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("title")}</FormLabel>
                        <FormControl>
                          <Input
                            className="rounded-lg border-border bg-card py-6 placeholder:opacity-70"
                            placeholder='ex: "Pyramids of Giza"'
                            autoComplete="title"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {!isChatWithArchitect && (
                    <FormField
                      control={form.control}
                      name="description"
                      render={({ field }) => (
                        <FormItem
                          className={cn(
                            !form.watch("isSearchable") && "opacity-30",
                          )}
                        >
                          <FormLabel>{t("description")}</FormLabel>
                          <FormControl>
                            <div className="relative">
                              <Input
                                disabled={!form.watch("isSearchable")}
                                className="rounded-lg border-border bg-card py-6 pr-9 placeholder:opacity-70"
                                type="text"
                                placeholder={t(
                                  "editChat.descriptionPlaceholder",
                                )}
                                autoComplete="description"
                                {...field}
                              />
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}

                  {!isChatWithArchitect && <ThreadSettingsFields form={form} />}
                </form>
              </Form>
            </div>
            <ScrollBar orientation="vertical" />
          </ScrollArea>

          <DialogFooter className="gap-2">
            <DialogClose asChild>
              <Button variant="outline" onClick={() => setOpenDialog(false)}>
                {t("cancel")}
              </Button>
            </DialogClose>

            <Button
              disabled={
                form.formState.isSubmitting ||
                // !form.formState.isValid ||
                isUploading
              }
              onClick={form.handleSubmit(onSubmit)}
            >
              {form.formState.isSubmitting ? t("saving") : t("save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
